import React, { ChangeEvent, useCallback } from 'react';
import { Control, UseFormSetValue, UseFormWatch } from 'react-hook-form';
import { Grid, TextField, Tooltip, Typography } from '@mui/material';
import { InfoOutlined, InsightsOutlined } from '@mui/icons-material';
import { FormCard, FormInput } from 'components';
import { DealTypes } from 'api';
import {
  getCalculatedCPM,
  getCTR,
  getEngagementRate,
  getRPM,
} from 'helpers/calculations';
import { formatCurrency, formatPercent } from 'helpers/formatters';

interface Props {
  data: DealTypes.ItemData | undefined;
  control: Control<DealTypes.ItemCreateParams> | undefined;
  watch: UseFormWatch<DealTypes.ItemCreateParams>;
  setValue: UseFormSetValue<DealTypes.ItemCreateParams>;
}

const InfluencerInsightsBlock = (props: Props): JSX.Element | null => {
  const { control, setValue, data, watch } = props;

  const reach = watch('actual_reach');
  const ctr = watch('ctr') || 0;
  const rpm = watch('rpm') || 0;

  const handleActualReachChange = useCallback(
    (
      event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
      onChange: (value: string | number | undefined) => void,
    ) => {
      const value = event.target.value ? +event.target.value : '';

      if (value) {
        const budget = getCalculatedCPM({
          budgetActual: data?.budget_actual,
          reachActual: value,
        });

        setValue('calculated_cpm', budget);

        const clicks = watch('clicks');
        const ctr = getCTR({
          clicks,
          reachActual: value,
        });
        setValue('ctr', ctr);

        const engagement = watch('engagement');

        const engagementRate = getEngagementRate({
          engagement,
          reachActual: value,
        });
        setValue('engagement_rate_pc', engagementRate);

        if (data?.revenue_actual) {
          const rpm = getRPM({
            revenue: data.revenue_actual,
            reachActual: value,
          });

          setValue('rpm', rpm);
        }
      }

      onChange(value);
    },
    [data?.budget_actual, data?.revenue_actual, setValue, watch],
  );

  const handleClicksChange = useCallback(
    (
      event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
      onChange: (value: string | number | undefined) => void,
    ) => {
      const value = event.target.value ? +event.target.value : '';

      if (value) {
        const reachActual = watch('actual_reach');
        const ctr = getCTR({
          clicks: value,
          reachActual,
        });
        setValue('ctr', ctr);
      }

      onChange(value);
    },
    [setValue, watch],
  );

  const handleEngagementChange = useCallback(
    (
      event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
      onChange: (value: string | number | undefined) => void,
    ) => {
      const value = event.target.value ? +event.target.value : '';

      if (value) {
        const reachActual = watch('actual_reach');
        const engagementRate = getEngagementRate({
          engagement: value,
          reachActual,
        });
        setValue('engagement_rate_pc', engagementRate);
      }

      onChange(value);
    },
    [setValue, watch],
  );

  return (
    <FormCard title="Influencer insights" avatar={<InsightsOutlined />}>
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <FormInput
            control={control}
            name="actual_reach"
            inputType="number"
            label="Actual reach"
            onChange={handleActualReachChange}
          />
        </Grid>
        <Grid item xs={6}>
          <FormInput
            control={control}
            name="avg_overall_reach"
            inputType="number"
            label="Avg overall reach"
          />
        </Grid>
        <Grid item xs={6}>
          <FormInput
            control={control}
            name="calculated_cpm"
            inputType="number"
            label="Actual CPM"
            tooltip={`
              Calculated by formula "calculated_cpm = (budget_actual / actual_reach) * 1000". 
              Actual budget available only after deal is created. 
              Field value is recalculated after "Actual reach" value is changed.
              Current values are: Actual budget = ${data?.budget_actual}, actual reach = ${reach}.
            `}
          />
        </Grid>
        <Grid item xs={6}>
          <FormInput
            control={control}
            name="impressions"
            inputType="number"
            label="Impressions"
          />
        </Grid>
        <Grid item xs={6}>
          <FormInput
            control={control}
            name="clicks"
            inputType="number"
            label="Clicks"
            onChange={handleClicksChange}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            name="ctr"
            label={
              <Typography display="flex" gap={1}>
                CTR
                <Tooltip
                  title={`Calculated by formula "ctr = clicks / actual_reach". Field value is recalculated after "Actual reach" or "Clicks" value is changed`}
                >
                  <InfoOutlined fontSize="small" />
                </Tooltip>
              </Typography>
            }
            value={formatPercent(ctr)}
            placeholder="CTR"
            disabled
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            name="rpm"
            label={
              <Typography display="flex" gap={1}>
                RPM
                <Tooltip
                  title={`Calculated by formula "rpm = revenue_actual / actual_reach * 1000". Field value is recalculated after "Actual reach" value is changed and revenue value is present`}
                >
                  <InfoOutlined fontSize="small" />
                </Tooltip>
              </Typography>
            }
            value={formatCurrency(rpm)}
            placeholder="RPM"
            disabled
          />
        </Grid>
        <Grid item xs={6}>
          <FormInput
            control={control}
            name="engagement"
            inputType="number"
            label="Engagement"
            onChange={handleEngagementChange}
          />
        </Grid>
        <Grid item xs={6}>
          <FormInput
            control={control}
            name="engagement_rate_pc"
            inputType="number"
            label="Engagement Rate %"
            disabled
            tooltip={`
              Calculated by formula "engagement_rate_pc = (engagement / actual_reach) * 100". 
              Actual budget available only after deal is created. 
              Field value is recalculated after "Actual reach" value is changed.
              Current values are: Actual engagement = ${data?.engagement}, actual reach = ${reach}.
            `}
          />
        </Grid>
      </Grid>
    </FormCard>
  );
};

export default InfluencerInsightsBlock;
